import { confirmDialog } from 'primereact/confirmdialog';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo, useState } from 'react';
import slugify from 'react-slugify';
import PageWrapper from '../../../../components/Display/PageWrapper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { privateFetch } from '../../../../utils/apiHelper';
import MultipleImagePanel from '../../common/image/MultipleImagePanel';
import ShowItem from './ShowItem';
import ShowPanel from './ShowPanel';
import styles from "../../../../components/Display/Wrapper.module.scss";

function ShowPage({ isActive }) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedShow, setSelectedShow] = useState(null);
  const [showPanelIsOpen, setShowPanelIsOpen] = useState(false);
  const [imagePanelIsOpen, setImagePanelIsOpen] = useState(false);
  const [showList, setShowlist] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { accountContext } = useAccountContext();

  const filteredShows = useMemo(() => {
    if (!debouncedQuery) return showList;
    return showList.filter((s) => slugify(s.title).includes(slugify(debouncedQuery)));
  }, [debouncedQuery, showList]);

  useEffect(() => {
    fetchShowList();
    fetchPropertyList();
  }, []);

  useEffect(() => {
    setLoading(true);

    const timeout = setTimeout(() => {
      setDebouncedQuery(searchInput);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchShowList = async () => {
    await privateFetch('GET', 'nouvelleparenthese', `/cu/${accountContext.id}/show/`).then(
      (res) => {
        if (res) {
          setShowlist(res);
          if (selectedShow) {
            setSelectedShow(res.find((show) => selectedShow.id === show.id));
          }
        }
      }
    );
  };

  const fetchPropertyList = async () => {
    await privateFetch(
      'GET',
      'resources',
      `/cu/${accountContext.id}/property/?types=np_theme,np_location`
    ).then((res) => {
      if (res) setPropertyList(res);
    });
  };

  const createShow = async (showForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'nouvelleparenthese',
      `/cu/${accountContext.id}/show/`,
      'La prestation a bien été ajoutée',
      JSON.stringify(showForm)
    ).then(() => {
      fetchShowList();
      setLoading(false);
      setShowPanelIsOpen(false);
    });
  };

  const updateShowImages = async (images) => {
    await privateFetch(
      'PUT',
      'nouvelleparenthese',
      `/cu/${accountContext.id}/show/${selectedShow.id}/images/`,
      'Les images de la prestation ont bien été modifiées',
      JSON.stringify({
        images: images.map((image) => image.id)
      })
    ).then(() => {
      fetchShowList();
      // setImagePanelIsOpen(false);
      setLoading(false);
    });
  };

  const updateShow = async (showForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'nouvelleparenthese',
      `/cu/${accountContext.id}/show/${selectedShow.id}/`,
      'La prestation a bien été modifiée',
      JSON.stringify(showForm)
    ).then(() => {
      fetchShowList();
      setLoading(false);
      setShowPanelIsOpen(false);
    });
  };

  const deleteShow = async (show) => {
    await privateFetch(
      'DELETE',
      'nouvelleparenthese',
      `/cu/${accountContext.id}/show/${show.id}/`,
      'La prestation a bien été supprimée'
    ).then(() => fetchShowList());
  };

  const confirmDeletion = (show) => {
    confirmDialog({
      message: 'Êtes-vous sûr de vouloir supprimer cette prestation ?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: () => deleteShow(show)
    });
  };

  const headerSearchBar = useMemo(
    () => (
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search"> </InputIcon>
        <InputText
          placeholder="Rechercher une prestation"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="rounded-full"
        />
      </IconField>
    ),
    [searchInput]
  );

  const newShowAction = {
    onClick: () => {
      setShowPanelIsOpen(true);
      setSelectedShow(undefined);
    },
    label: 'Ajouter une prestation',
    icon: 'pi-plus'
  };

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper label="Prestations" actions={[newShowAction]} search={headerSearchBar}>
        <div className={styles.pageContentContainer}>
          <div className={` ${styles.gridLayout}`}>

            {filteredShows.map((show) => (
              <ShowItem
                key={show.id}
                show={show}
                updateShow={() => {
                  setSelectedShow(show);
                  setShowPanelIsOpen(true);
                }}
                deleteShow={() => confirmDeletion(show)}
                manageImages={() => {
                  setSelectedShow(show);
                  setImagePanelIsOpen(true);
                }}
              />
            ))}
            {!filteredShows.length && (
              <span className="text-sm italic text-slate-600">Aucune prestation à afficher</span>
            )}
          </div>
        </div>
      </PageWrapper>

      <ShowPanel
        key="show"
        loading={loading}
        show={selectedShow}
        isOpen={showPanelIsOpen}
        onClose={() => {
          setShowPanelIsOpen(false);
        }}
        onCreate={createShow}
        onUpdate={updateShow}
        propertyList={propertyList}
      />

      <MultipleImagePanel
        loading={loading}
        isOpen={imagePanelIsOpen}
        onClose={() => {
          setImagePanelIsOpen(false);
        }}
        images={selectedShow?.images}
        onValidate={updateShowImages}
      />
    </div>
  );
}

export default ShowPage;
